import { Box, Button, Dialog, DialogActions, DialogTitle, Stack, TextField } from '@mui/material'
import { FormEvent, FormEventHandler } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Group, ID, User } from '../../types/models'
import { callAxios } from '../../hooks/useAxios'
import { ModalActionType } from '../../pages/AdminUserGroupsPage'
import { Safety } from '../../classes/Safety'
import { useApp } from '../../hooks/useApp'

interface UserGroupsField {
  dataCy: string
  label: string
  inputName: string
}

interface GroupActionsFormProps {
    parentGroup: Group
    modalType: ModalActionType | ''
    openUserGroupModal: boolean
    setOpenUserGroupModal: (state: boolean) => void
    setModalType: (state: '' | ModalActionType) => void
}

export function GroupActionsForm(props: GroupActionsFormProps): React.ReactElement {
  const { parentGroup, modalType, setModalType, openUserGroupModal, setOpenUserGroupModal } = props
  const { t } = useTranslation()
  const app = useApp()

  const onSubmit: FormEventHandler<HTMLFormElement> = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data: { [codeName: string]: string } = {}
    for (const pair of formData.entries()) {
      data[pair[0]] = pair[1] as string
    }

    switch (modalType) {
      case 'CREATE_GROUP':
        callAxios<Group>({
          method: 'POST',
          url: '/user-groups',
        }, {
          parent_group_id: parentGroup.id,
          name: data.name
        }).then(res => {
          res.success && setOpenUserGroupModal(false)
          if (!res.success) app.addError(res)
        })
        break
      case 'CREATE_ADMIN':
        // Create a new user (READER)
        callAxios<User>({
          method: 'POST',
          url: '/users',
        }, {
          area: 'FI',
          country: 'FI',
          currency: 'EUR',
          email: data.email.trim(),
          defaultLocation: t('Home'),
          defaultGroup: t('Default group for {email}').replace('{email}', data.email.trim()),
          language: Safety.get('language'),
          phone: '',
          role: 'ADMIN',
          parent_group_id: parentGroup.id as ID,
        }).then(res => {
          // Grant admin permission
          if (res.success) {
            callAxios<User>({
              method: 'PATCH',
              url: `/user-groups/${parentGroup.id}/users/${res.data.id}`,
            }, {
              role: 'ADMIN'
            }).then(res => res.success && setOpenUserGroupModal(false))
          } else {
            app.addError(res)
          }
        })
        break
      case 'CREATE_USER':
        callAxios<User>({
          method: 'POST',
          url: '/users',
        }, {
          area: 'FI',
          country: 'FI',
          currency: 'EUR',
          email: data.email.trim(),
          defaultLocation: t('Home'),
          defaultGroup: t('Default group for {email}').replace('{email}', data.email.trim()),
          language: Safety.get('language'),
          phone: '',
          parent_group_id: parentGroup.id as ID,
        }).then(res => {
          res.success && setOpenUserGroupModal(false)
          if (!res.success) app.addError(res)
        })
        break
    }
  }

  const handleCloseInstallerPanel = () => {
    setOpenUserGroupModal(false)
    setModalType('')
  }

  const conditionalFields: () => (string | UserGroupsField)[] | undefined = () => {
    switch (modalType) {
      case 'CREATE_GROUP':
        return [
          t('Please enter name to create a new user group'),
          {
            dataCy: 'Create user group-Name',
            label: t('Name?'),
            inputName: 'name'
          },
        ]
      case 'CREATE_ADMIN':
        return [
          t('Please enter email address to create a new admin for this group'),
          {
            dataCy: 'Add existing customer-Email address',
            label: t('Email Address'),
            inputName: 'email'
          },
        ]
      case 'CREATE_USER':
        return [
          t('Please enter email address to create a new user for this group'),
          {
            dataCy: 'Create new user-Email',
            label: t('Email Address'),
            inputName: 'email'
          },
        ]
    }
  }

  return (
    <Box >
        <Dialog aria-modal={true} open={!!openUserGroupModal} onClose={() => setOpenUserGroupModal(false)}>
            <form id="user-group-form" onSubmit={onSubmit}>
                <DialogTitle sx={{ backgroundColor: 'blue' }}>{modalType !== '' ? t(`Option-${modalType}`) : ''}</DialogTitle>
                <Box sx={{ px: 2, pt: 2 }}>
                    {conditionalFields()?.map(field => {
                      if (typeof field === 'string') {
                        return <Box id={field} key={field}>{field}</Box>
                      } else {
                        return <TextField
                          id={field.dataCy}
                          key={field.dataCy}
                          data-cy={`text-${field.dataCy}`}
                          margin="normal"
                          fullWidth
                          name={field.inputName}
                          label={field.label}
                        />
                      }
                    })}
                </Box>
                <DialogActions>
                    <Stack direction="row" spacing={2} sx={{ m: 2 }}>
                        <Button color="primary" onClick={handleCloseInstallerPanel}><Trans>Cancel</Trans></Button>
                        <Button data-cy="button-Installer level OK" color="secondary" type='submit'><Trans>OK</Trans></Button>
                    </Stack>
                </DialogActions>
            </form>
        </Dialog>
    </Box>
  )
}
